"use client";

import { Anchor, Box, Grid, Text, useMantineTheme } from "@mantine/core";
import AutoFixHighRounded from "@mui/icons-material/AutoFixHighRounded";
import GroupRounded from "@mui/icons-material/GroupRounded";
import QueryStatsRounded from "@mui/icons-material/QueryStatsRounded";
import RocketRounded from "@mui/icons-material/RocketRounded";
import SavingsRounded from "@mui/icons-material/SavingsRounded";
import StackedBarChartRounded from "@mui/icons-material/StackedBarChartRounded";
import { SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import classes from "./feature-module.module.css";
import AboutFeatureTab from "./AboutFeatureTab";

type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
  muiName: string;
};

interface FeatureItem {
  title: string;
  description: React.ReactNode;
  icon: MuiIcon;
}

export default function FeatureModule() {
  const theme = useMantineTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const contentPanelRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    contentPanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [activeIndex]);

  const featureTextProps = {
    size: "md",
    mt: "md",
    color: theme.colors.gray[0],
    className: classes.contentText,
  };

  const featureData: FeatureItem[] = [
    {
      title: "Advanced Analytics",
      icon: QueryStatsRounded,
      description: (
        <>
          <Text {...featureTextProps}>
            We&apos;re the <strong>only site</strong> that offers full IB STEM
            coverage with integrated performance tracking, and syllabus tools -
            all in one place.
          </Text>
          <Text {...featureTextProps}>
            Understand with our videos. Reinforce with notes, flashcards and
            cram sheets. Develop with our questions and tests. Then track your
            progress and see how you compare. That&apos;s the Tiber difference.
          </Text>
        </>
      ),
    },
    {
      title: "Proven results",
      icon: AutoFixHighRounded,
      description: (
        <>
          <Text {...featureTextProps}>
            Over <strong>85,000 students</strong> use Tiber Tutor. Our users
            outperform the IB - and we&apos;re the only platform that tracks it.
            Our{" "}
            <Anchor
              href="/#resources"
              component={Link}
              className={classes.anchor}
            >
              resources
            </Anchor>{" "}
            are built by the same people who set and mark the exams.
          </Text>
          <Text {...featureTextProps}>
            See what they say on our{" "}
            <Anchor
              href="/#reviews"
              component={Link}
              className={classes.anchor}
            >
              homepage
            </Anchor>
            . Members call it &quot;life changing.&quot;
          </Text>
        </>
      ),
    },
    {
      title: "Complete Coverage",
      icon: StackedBarChartRounded,
      description: (
        <>
          <Text {...featureTextProps}>
            We built Tiber Tutor to be the{" "}
            <strong>most comprehensive IB STEM platform</strong> - with
            integrated performance tracking, syllabus completion, and
            examiner-quality content in one place.
          </Text>
          <Text {...featureTextProps}>
            <Anchor href="/team" component={Link} className={classes.anchor}>
              Our team
            </Anchor>{" "}
            has spent over a decade creating{" "}
            <Anchor
              href="/#resources"
              component={Link}
              className={classes.anchor}
            >
              our resources
            </Anchor>{" "}
            while delivering elite IB tuition. The result: seriously good
            grades, and a platform that actually measures progress.
          </Text>
        </>
      ),
    },
    {
      title: "Examiner-Built",
      icon: GroupRounded,
      description: (
        <>
          <Text {...featureTextProps}>
            Tiber Tutor is built by an{" "}
            <Anchor href="/team" component={Link} className={classes.anchor}>
              exclusive collection of examiners and teachers
            </Anchor>
            , each with over a decade of experience teaching purely IB STEM -
            and thousands of hours in the classroom.
          </Text>
          <Text {...featureTextProps}>
            Based around the world, our experts are the people who write exams
            and mark them. No AI, no shortcuts: just the expertise that gets
            students to 7&apos;s.
          </Text>
        </>
      ),
    },
    {
      title: "Unbeatable value",
      icon: SavingsRounded,
      description: (
        <>
          <Text {...featureTextProps}>
            Our teachers are experts who charge <strong>over $400/h</strong> for
            1-on-1 tuition. Tiber Tutor packs their knowledge into one
            subscription - for a fraction of that cost.
          </Text>
          <Text {...featureTextProps}>
            Try it risk-free: <strong>7-day free trial</strong> for everyone, no
            payment required. See why 500+ schools and 85,000+ students trust
            us.
          </Text>
        </>
      ),
    },
    {
      title: "Always growing",
      icon: RocketRounded,
      description: (
        <>
          <Text {...featureTextProps}>
            We&apos;re committed to being the most reliable IB STEM resource
            online. New content is added constantly - and every update is
            included in your membership at no extra cost.
          </Text>
          <Text {...featureTextProps}>
            <Anchor href="/team" component={Link} className={classes.anchor}>
              Our team
            </Anchor>{" "}
            is forward-thinking: we believe our users deserve to be rewarded.
            All future resources are yours, for free, for as long as you&apos;re
            with us.
          </Text>
        </>
      ),
    },
  ];

  const activeFeature = featureData[activeIndex];

  return (
    <Grid grow className={classes.grid} gutter="3rem">
      <Grid.Col span={{ base: 12, md: 5 }}>
        <Box className={classes.tabsColumn}>
          {featureData.map((item, index) => (
            <AboutFeatureTab
              key={item.title}
              title={item.title}
              icon={item.icon}
              isActive={activeIndex === index}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </Box>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 7 }}>
        <Box ref={contentPanelRef} className={classes.contentPanel}>
          <Box key={activeIndex} className={classes.contentInner}>
            {activeFeature.description}
          </Box>
        </Box>
      </Grid.Col>
    </Grid>
  );
}
